import {
  isPortfolioClaimSignUpContent,
  PORTFOLIO_CLAIM_SIGN_UP_CONTENT_BY_ARM,
  type PortfolioClaimArm,
} from '@extern/portfolio/utils/portfolio-sign-up-url';

import { prefilledEmail } from '@/app/(auth)/_utils/prefilled-email';
import {
  getPortfolioPublicBaseUrl,
  PORTFOLIO_PUBLIC_PATH,
  validatePortfolioUsername,
} from '@/features/portfolio/_utils/username';

/**
 * A visitor who built and claimed a portfolio on a landing page, then pressed "Create my account".
 *
 * 🔴 READ FROM THE URL ONLY, NEVER FROM THE PERSISTED `__utm_*`. The claim flow writes everything
 * this needs onto the sign-up link (`buildClaimSignUpUrl`). The persisted UTMs outlive the visit, so
 * reading them would show "your portfolio is live" to the same browser on an ordinary sign-up a week
 * later. Same rule the Labs arrival follows.
 *
 * INERT FOR ORDINARY SIGN-UP TRAFFIC. Without a claim `utm_content` AND a well-formed portfolio URL
 * this is null, and sign-up renders byte-for-byte as it ships.
 */
export interface PortfolioArrival {
  /** Which landing arm produced the portfolio; null for the arm-less `portfolio_new_claim`. */
  arm: PortfolioClaimArm | null;
  /** The address they claimed with, or '' when the link did not carry a usable one. */
  email: string;
  /** Canonical public URL, rebuilt from our own base and the validated slug, never echoed from input. */
  publicUrl: string;
  slug: string;
}

const LEADING_WWW = /^www\./;
const TRAILING_SLASHES = /\/+$/;

function armFromContent(utmContent: string): PortfolioClaimArm | null {
  const arms = Object.keys(PORTFOLIO_CLAIM_SIGN_UP_CONTENT_BY_ARM) as PortfolioClaimArm[];
  return arms.find((arm) => PORTFOLIO_CLAIM_SIGN_UP_CONTENT_BY_ARM[arm] === utmContent) ?? null;
}

const WEB_PROTOCOLS = ['http:', 'https:'];

/**
 * The slug, when `portfolio_url` is a web URL whose path is `/portfolios/{valid slug}` and nothing else.
 *
 * 🔴 THE HOST IS DELIBERATELY NOT CHECKED. The same portfolio app answers on several hosts, and the
 * one the landing page hands over is not always the one this app is configured with: on dev the claim
 * flow sends `extern-portfolio-dev.vercel.app` while `NEXT_PUBLIC_PORTFOLIO_PUBLIC_BASE_URL` is
 * `public-portfolio-dev.extern.com`, so a host comparison silently dropped every dev arrival back to
 * plain sign-up (review on #3002). Comparing hosts also bought nothing: only the slug is taken from
 * this value. The address shown and the summary fetched are both rebuilt from OUR configured base, so
 * a foreign host here can do no more than our own host with the same slug already could.
 */
function slugFromPortfolioUrl(raw: string | null): string | null {
  if (!raw) {
    return null;
  }

  let candidate: URL;
  try {
    candidate = new URL(raw);
  } catch {
    return null;
  }

  if (!WEB_PROTOCOLS.includes(candidate.protocol)) {
    return null;
  }

  const prefix = `${PORTFOLIO_PUBLIC_PATH}/`;
  const path = candidate.pathname.replace(TRAILING_SLASHES, '');
  if (!path.startsWith(prefix)) {
    return null;
  }

  const username = validatePortfolioUsername(path.slice(prefix.length));
  return username.valid ? username.normalizedUsername : null;
}

export function parsePortfolioArrival(searchParams: Pick<URLSearchParams, 'get'> | null): PortfolioArrival | null {
  const utmContent = searchParams?.get('utm_content') ?? null;
  if (!(utmContent && isPortfolioClaimSignUpContent(utmContent))) {
    return null;
  }

  const slug = slugFromPortfolioUrl(searchParams?.get('portfolio_url') ?? null);
  if (!slug) {
    return null;
  }

  const publicBaseUrl = getPortfolioPublicBaseUrl();

  return {
    arm: armFromContent(utmContent),
    email: prefilledEmail(searchParams),
    publicUrl: `${publicBaseUrl}/${slug}`,
    slug,
  };
}

const PROTOCOL = /^https?:\/\//;

/** "extern.com/portfolios/jane-chen": the address as a person would say it, for display only. */
export function portfolioDisplayAddress(publicUrl: string): string {
  return publicUrl.replace(PROTOCOL, '').replace(LEADING_WWW, '');
}
